import { useCallback, useMemo, useRef, useState } from 'react';
import { Stack, Title, Text, Group, Button, Loader, Alert } from '@mantine/core';
import { useDebouncedValue, useHotkeys, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { usePlaylists } from '../usePlaylists';
import { useVideos } from '@/features/media/useVideos';
import { useStreams } from '@/features/streams/useStreams';
import { PlaylistUtils } from '../PlaylistUtils';
import { PlaylistToolbar } from './PlaylistToolbar';
import { PlaylistGrid } from './PlaylistGrid';
import { CreatePlaylistModal } from './CreatePlaylistModal';
import { PlaylistPublishModal } from './PlaylistPublishModal';
import { SelectionProvider, useSelection } from '@/shared/contexts/SelectionContext';
import {
  useActivatePlaylists,
  useDeactivatePlaylists,
  useDeletePlaylists,
} from '../usePlaylistMutations';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { SearchDebounce } from '@/shared/utils/searchDebounce';
import { usePlaylistGridEngine } from '../hooks/usePlaylistGridEngine';
import { usePlaylistGridState } from '../stores/playlistGridState';
import type { AnyPlaylistData, PlaylistGridRow, PlaylistSortKey } from '@/lib/types/Playlist';

function PlaylistsPageContent() {
  const { selectedCId } = useAuth();
  const { data: playlistData, isLoading: playlistsLoading, isError, error } = usePlaylists();

  // Tag playlist match counts need the media source matching each playlist's
  // mediaType — only fetch a source when a tag playlist actually uses it
  // (skips the heavy videos payload on stream-only accounts and vice versa).
  // refetchOnMount: false — the ~count column tolerates cached data, so
  // revisiting the list page costs zero network while the cache is warm.
  const needsVideoCounts = playlistData?.tagLists.some(t => t.mediaType === 'video') ?? false;
  const needsStreamCounts = playlistData?.tagLists.some(t => t.mediaType === 'video_stream') ?? false;
  const { videos, isLoading: videosLoading } = useVideos({ enabled: needsVideoCounts, refetchOnMount: false });
  const { streams, isLoading: streamsLoading } = useStreams({ enabled: needsStreamCounts, refetchOnMount: false });
  const { selectedIds, clearSelection } = useSelection();
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [publishPlaylist, setPublishPlaylist] = useState<AnyPlaylistData | null>(null);

  const searchText = usePlaylistGridState((s) => s.searchText);
  const setSearchText = usePlaylistGridState((s) => s.setSearchText);
  const searchMode = usePlaylistGridState((s) => s.searchMode);
  const setSearchMode = usePlaylistGridState((s) => s.setSearchMode);
  const sortKey = usePlaylistGridState((s) => s.sortKey);
  const sortDirection = usePlaylistGridState((s) => s.sortDirection);
  const setSort = usePlaylistGridState((s) => s.setSort);
  const setScrollTop = usePlaylistGridState((s) => s.setScrollTop);

  const initialScrollTopRef = useRef(usePlaylistGridState.getState().scrollTop);

  const activateMutation = useActivatePlaylists();
  const deactivateMutation = useDeactivatePlaylists();
  const deleteMutation = useDeletePlaylists();

  useHotkeys([
    ['Escape', () => clearSelection()],
  ]);

  const gridRows = useMemo(() => {
    if (!playlistData) return [];
    const videosData = needsVideoCounts && !videosLoading ? videos : undefined;
    const streamsData = needsStreamCounts && !streamsLoading ? streams : undefined;
    return PlaylistUtils.toGridRows(playlistData, videosData, streamsData);
  }, [playlistData, videos, videosLoading, needsVideoCounts, streams, streamsLoading, needsStreamCounts]);

  const [debouncedSearchText] = useDebouncedValue(searchText, SearchDebounce.forItemCount(gridRows.length));

  const gridEngine = usePlaylistGridEngine({
    customerId: selectedCId,
    playlists: gridRows,
    sort: {
      key: sortKey,
      direction: sortDirection,
    },
    search: {
      mode: searchMode,
      text: debouncedSearchText,
    },
  });

  // Selections survive search changes; surface how many selected rows the
  // current search is hiding so the count never looks "wrong".
  const hiddenSelectedCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    const visible = new Set(gridEngine.orderedIds);
    let hidden = 0;
    for (const id of selectedIds) {
      if (!visible.has(id)) hidden++;
    }
    return hidden;
  }, [selectedIds, gridEngine.orderedIds]);

  const handleSort = useCallback((key: PlaylistSortKey) => {
    setSort(key);
  }, [setSort]);

  const handleBulkActivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    activateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Activated', message: `${ids.length} playlist(s) activated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, activateMutation, clearSelection]);

  const handleBulkDeactivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    deactivateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Deactivated', message: `${ids.length} playlist(s) deactivated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, deactivateMutation, clearSelection]);

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    modals.openConfirmModal({
      title: 'Delete Playlists',
      children: (
        <Text size="sm">
          Are you sure you want to delete {ids.length} playlist(s)? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate(ids, {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `${ids.length} playlist(s) deleted.`, color: 'green' });
            clearSelection();
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [selectedIds, deleteMutation, clearSelection]);

  // Grid rows don't carry cId, which the publish modal needs — resolve the row
  // back to the full playlist record (same pattern as VideosPage handlePublish).
  const playlistsById = useMemo(() => {
    const byId = new Map<number, AnyPlaylistData>();
    playlistData?.customLists.forEach((p) => byId.set(p.listId, p));
    playlistData?.tagLists.forEach((p) => byId.set(p.listId, p));
    return byId;
  }, [playlistData]);

  const handleRowPublish = useCallback((playlist: PlaylistGridRow) => {
    const full = playlistsById.get(playlist.listId);
    if (full) setPublishPlaylist(full);
  }, [playlistsById]);

  const handleRowActivateToggle = useCallback((playlist: PlaylistGridRow) => {
    const mutation = playlist.isActive ? deactivateMutation : activateMutation;
    const action = playlist.isActive ? 'Deactivated' : 'Activated';
    mutation.mutate([playlist.listId], {
      onSuccess: () => {
        notifications.show({ title: action, message: `"${playlist.title}" ${action.toLowerCase()}.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [activateMutation, deactivateMutation]);

  const handleRowDelete = useCallback((playlist: PlaylistGridRow) => {
    deleteMutation.mutate([playlist.listId], {
      onSuccess: () => {
        notifications.show({ title: 'Deleted', message: `"${playlist.title}" deleted.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [deleteMutation]);

  if (playlistsLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading playlists...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load playlists'}
      </Alert>
    );
  }

  return (
    <Stack gap="md" h="100%" style={{ position: 'relative', minHeight: 0 }}>
      <Group justify="space-between">
        <Title order={2}>Playlists</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
          Create Playlist
        </Button>
      </Group>

      <PlaylistToolbar
        searchText={searchText}
        onSearchChange={setSearchText}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
        totalCount={gridRows.length}
        resultCount={gridEngine.resultCount}
        hiddenSelectedCount={hiddenSelectedCount}
        onBulkActivate={handleBulkActivate}
        onBulkDeactivate={handleBulkDeactivate}
        onBulkDelete={handleBulkDelete}
        activateLoading={activateMutation.isPending}
        deactivateLoading={deactivateMutation.isPending}
        deleteLoading={deleteMutation.isPending}
      />

      <PlaylistGrid
        count={gridEngine.count}
        allIds={gridEngine.orderedIds}
        getRow={gridEngine.getRowByIndex}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        onPublish={handleRowPublish}
        onActivateToggle={handleRowActivateToggle}
        onDelete={handleRowDelete}
        initialScrollTop={initialScrollTopRef.current}
        onScrollTopChange={setScrollTop}
      />

      <CreatePlaylistModal opened={createModalOpened} onClose={closeCreateModal} />

      {publishPlaylist && (
        <PlaylistPublishModal
          opened={!!publishPlaylist}
          onClose={() => setPublishPlaylist(null)}
          playlist={publishPlaylist}
        />
      )}
    </Stack>
  );
}

export function PlaylistsPage() {
  return (
    <SelectionProvider>
      <PlaylistsPageContent />
    </SelectionProvider>
  );
}
